/**
 * `scan.alignment.summary` — the struggle record for the posture-alignment step.
 *
 * This is the thing the whole telemetry effort was started for. The original
 * idea was to replicate the canvas joint indicators as DOM so a session recorder
 * would capture them; that was rejected (PLAN.md §11.4) because it produces one
 * watchable session at a cost that scales with how badly the user is struggling.
 * What the team actually wants to know is:
 *
 *   which joint was out of position, for how long, did it EVER get in, how many
 *   times did the user get it and lose it, what was the app telling them while
 *   that happened, and was the device even keeping up?
 *
 * All of that is a fixed-size record, emitted once, queryable across every
 * session ever recorded.
 *
 * ── The hard constraint ──────────────────────────────────────────────────
 *
 * `emitJointCoordinates` fires at up to 15 Hz and `calculateGuidance` at 2 Hz.
 * NOTHING here may write to Firestore, allocate per tick, or do work
 * proportional to anything. Each tick is a handful of number additions on
 * pre-allocated fields. See PLAN.md §11.1 — the scan path is already running
 * MoveNet plus EfficientDet on a phone.
 *
 * ── The privacy line ─────────────────────────────────────────────────────
 *
 * Durations, counts and booleans DERIVED from keypoints. Never a coordinate,
 * never a time series. `jointOutOfAreaMs` is a duration; a stream of wrist
 * positions would be body-derived data leaving to a third party (PLAN.md §11.2).
 */

import { PoseDetectionManager } from "@/services/detection/PoseDetectionManager";
import { getDeviceMetrics } from "@/services/detection/deviceCapability";
import {
  PHONE_RATE_MIN_SPAN_MS,
  PHONE_RATE_WINDOW_MS,
  blindWhileVisible,
  detectorHealth,
  jointUnseenWhileFramed,
  phoneTooSlow,
  type JointSightSample,
} from "@/services/detection/liveHealth";
import { alignmentVideoFreeze } from "@/services/detection/videoFreeze";
import { logEvent } from "@/Utils/logger";
import { Events, Outcomes, Phases, type Outcome } from "./events";
import { onPhaseTransition } from "./phases";

const JOINTS = ["left_wrist", "right_wrist", "left_ankle", "right_ankle"] as const;
type Joint = (typeof JOINTS)[number];

/** Reused every frame: the per-emit path must not allocate. */
const sight: JointSightSample = {
  source: "device",
  chargeMs: 0,
  framed: false,
  unseen: [false, false, false, false],
};

/**
 * A gap longer than this between `jointCoordinates` emits means no pose was
 * detected at all — the person stepped out of frame, or detection stalled.
 * Detection runs at 15 Hz (~67ms), so 500ms is ~7 missed cycles: comfortably
 * past normal jitter, short enough to catch a real dropout.
 */
const POSE_LOST_GAP_MS = 500;

/** 128 buckets × 0.01 covers 0 → 1.28; a person can exceed the frame (>1). */
const HR_BUCKETS = 128;
const HR_BUCKET_WIDTH = 0.01;

/**
 * The band between `isTooFar` (<= 0.6) and `isTooClose` (> 0.95) — i.e. the
 * frame-fill fraction at which the app has nothing to correct.
 *
 * Time spent here is the discriminator that separates "this phone cannot keep
 * up" from "this room/framing is wrong". A session that never got into the band
 * had an environment problem, and no amount of device adaptation fixes it —
 * which is what keeps a badly-framed session out of any claim that the handset
 * is at fault. See PoseDetectionManager `calculateGuidance`.
 */
const HR_BAND_MIN = 0.6;
const HR_BAND_MAX = 0.95;

/**
 * Trailing timestamps, as fixed rings: the emits, and the phone's own frames.
 * Read by `getRecentPoseHz`, which is the live signal allowed to override the
 * boot-time device verdict.
 *
 * A growing array would be a per-tick allocation — the same defect the
 * heightRatio histogram replaced in [2.5.12]. 32 slots is >10s of window at
 * 3 Hz and ~2s at 15 Hz, which is the right shape: the slower the device, the
 * longer the window it is judged over.
 */
const RATE_WINDOW = 32;

/**
 * Inter-emit gaps, as a fixed histogram: 64 buckets of 25 ms covering 0-1600 ms,
 * with everything above landing in the last bucket. `effectivePoseHz` is a mean
 * and cannot show a stutter — a device emitting at 12 Hz with a 1 s freeze every
 * few seconds reports the same mean as one emitting evenly at a lower rate, and
 * those feel completely different to hold a pose against. Gaps past
 * POSE_LOST_GAP_MS are excluded, the same rule `poseLostMs` uses.
 */
const GAP_BUCKETS = 64;
const GAP_BUCKET_MS = 25;

/**
 * BUG-0010 item 2 — keypoint confidence, per joint, as a fixed histogram.
 *
 * `jointUndetectedMs` reports the RESULT of `keyPointInFrame`'s
 * `score > 0.3` comparison, and on the §0 session it read ~100% of visible time
 * for all four gate joints. One boolean cannot say which of three things caused
 * that, and the three want opposite fixes:
 *
 *   - scores near zero on every device      → the threshold is fine, the
 *                                             detector is failing, and the
 *                                             600 ms joint latch has never been
 *                                             reachable for anyone (H3);
 *   - healthy on iPhone, low on Android     → a real model-quality problem (H1);
 *   - healthy everywhere                    → the threshold is fine and the
 *                                             failure is somewhere else.
 *
 * 20 buckets of 0.05 over 0-1. The resolution is chosen so `keyPointInFrame`'s
 * threshold lands exactly on a bucket EDGE — 0.30 is the lower edge of bucket 6
 * — which makes "the p50 is below the threshold" readable straight off the
 * reported number instead of depending on where a bucket happens to straddle it.
 *
 * **The bucket is `floor(score * BUCKETS_PER_UNIT)`, never `floor(score / width)`,
 * and that is not a style preference.** Dividing by 0.05 in IEEE-754 puts a
 * score of exactly 0.30 in bucket 5 (`0.3 / 0.05` is 5.999999999999999), which
 * is precisely the edge this resolution was chosen to land on; 0.15, 0.60, 0.70
 * and 0.95 are wrong the same way. Multiplying by the integer count is exact at
 * every edge. Anything added here that buckets a fraction wants the same form.
 *
 * A confidence scalar derived from a keypoint is inside the privacy line in this
 * file's header; the coordinate it came with is not, and is not recorded.
 */
const SCORE_BUCKETS = 20;
const SCORE_BUCKETS_PER_UNIT = 20;

/**
 * BUG-0010 item 2 — the two ankle estimates' horizontal separation, as a
 * fraction of the distance between their two box CENTRES. 1.0 means the feet are
 * exactly as far apart as the boxes ask for.
 *
 * This is what separates H1 from H2, which `anklesCollapsedMs` structurally
 * cannot: a person standing with their feet together and a detector collapsing
 * both ankle estimates onto one leg produce the same duration, but not the same
 * distribution. A narrow stance is stable — a tight cluster somewhere below 1 —
 * and a collapsing detector is not.
 *
 * 40 buckets of 0.05 covers 0-2.0, with everything above in the last bucket. A
 * ratio can legitimately exceed 1 (feet wider than the boxes); the discriminating
 * region is 0-1.2 and the tail only has to not corrupt the median. Bucketed by
 * integer multiplication for the reason spelled out on `SCORE_BUCKETS` above.
 */
const SEP_BUCKETS = 40;
const SEP_BUCKETS_PER_UNIT = 20;

interface Acc {
  startedAt: number;
  lastTickAt: number;
  ticks: number;
  outMs: Record<string, number>;
  everIn: Record<string, boolean>;
  guidanceMs: Record<string, number>;
  lastGuidance: string;
  lastGuidanceAt: number;
  guidanceTransitions: number;
  correctPosture: boolean;
  correctPostureLostCount: number;
  firstCorrectPostureAt: number | null;
  /** When the running model was first judged unable to see a joint; ms from entry. */
  jointUnseenAt: number | null;
  poseLostMs: number;
  longTaskCount: number;
  longTaskMaxMs: number;
  /**
   * Distribution of the person's frame-fill fraction (see `getHeightRatio()`),
   * as a FIXED histogram: `HR_BUCKETS` buckets of `HR_BUCKET_WIDTH`, allocated
   * once per phase. A growing sample array was a per-tick allocation — ~9k
   * floats for a user stuck ten minutes — which the header rules out.
   * Min/max are exact scalars; the median comes from the histogram.
   */
  heightRatioHist: Uint32Array;
  heightRatioCount: number;
  heightRatioMin: number;
  heightRatioMax: number;
  /** Alternations between "Tilt Device Down" and "Tilt Device Up". */
  tiltOscillations: number;
  lastTiltDirection: "up" | "down" | null;
  /** BUG-0008: emits where the low-end inflation is what let a joint pass. */
  rescuedTicks: Record<string, number>;
  /** BUG-0008: cumulative visible time inside the followable heightRatio band. */
  heightRatioInBandMs: number;
  /**
   * v2.5.15 diagnostics. Every one of these answers a question the fields above
   * structurally cannot, and all of them are derived scalars — never a
   * coordinate, per the privacy line in this file's header.
   */
  /** Time the detector returned NO keypoint for a joint: its box is not drawn. */
  missingMs: Record<string, number>;
  /** Time a joint was present but unusable — score <= 0.3, or pinned at 0,0. */
  undetectedMs: Record<string, number>;
  /** Worst and mean overshoot outside the box, as a fraction of the box's size. */
  missMax: Record<string, number>;
  missSum: Record<string, number>;
  missCount: Record<string, number>;
  /** Visible time with N of the four joints simultaneously inside their boxes. */
  inAreaMs: Float64Array;
  /**
   * Per-joint confidence histogram, flattened: joint `i`'s buckets live at
   * `[i * SCORE_BUCKETS, (i+1) * SCORE_BUCKETS)`. One typed array rather than
   * four so the hot path touches one object.
   */
  scoreHist: Uint32Array;
  scoreCount: Uint32Array;
  scoreMax: Float64Array;
  /** Ankle-separation ratio: distribution, count, and the closest they got. */
  sepHist: Uint32Array;
  sepCount: number;
  sepMin: number;
  /** Visible time the emitted gate was true. */
  correctPostureMs: number;
  /** Time both ankle estimates sat closer together than their boxes' own gap. */
  anklesCollapsedMs: number;
  /** Distribution of the gap between emits; see GAP_BUCKETS. */
  gapHist: Uint32Array;
  gapMaxMs: number;
  /** BUG-0008: trailing emit timestamps for `getRecentPoseHz`. */
  rateRing: Float64Array;
  rateHead: number;
  rateCount: number;
  /**
   * Every frame the PHONE's detector completed, emitted or not: what Auto's
   * "phone too slow" judges (`getRecentPoseHz(…, { phoneOnly })`). A frame the
   * cloud detected empties it.
   */
  phoneRing: Float64Array;
  phoneHead: number;
  phoneCount: number;
  /** The latest phone frame was counted from its emit; its `frameDetected` must not count it again. */
  phoneFrameCounted: boolean;
}

let acc: Acc | null = null;
/**
 * Structurally typed rather than `PoseDetectionManager`, so this module reads
 * only what it needs and the test can mock exactly that surface.
 */
let poseManagerRef: {
  getHeightRatio: () => number;
  getPoseFrameSize: () => { width: number; height: number };
  getTuning: () => { jointLatchMs: number };
  hasSeenStrictPosture: () => boolean;
  getLatchRescuedEmits: () => number;
  getFloat32Enabled: () => boolean | null;
  getLastDetectSource: () => "device" | "server";
  getServerPoseStats: () => Record<string, unknown>;
  resetAttemptMetrics: () => void;
} | null = null;
let unsubscribers: Array<() => void> = [];
let longTaskObserver: PerformanceObserver | null = null;

function freshAcc(now: number): Acc {
  const outMs: Record<string, number> = {};
  const everIn: Record<string, boolean> = {};
  const rescuedTicks: Record<string, number> = {};
  const missingMs: Record<string, number> = {};
  const undetectedMs: Record<string, number> = {};
  const missMax: Record<string, number> = {};
  const missSum: Record<string, number> = {};
  const missCount: Record<string, number> = {};
  for (const j of JOINTS) {
    outMs[j] = 0;
    everIn[j] = false;
    rescuedTicks[j] = 0;
    missingMs[j] = 0;
    undetectedMs[j] = 0;
    missMax[j] = 0;
    missSum[j] = 0;
    missCount[j] = 0;
  }
  return {
    startedAt: now,
    lastTickAt: now,
    ticks: 0,
    outMs,
    everIn,
    guidanceMs: {},
    lastGuidance: "unknown",
    lastGuidanceAt: now,
    guidanceTransitions: 0,
    correctPosture: false,
    correctPostureLostCount: 0,
    firstCorrectPostureAt: null,
    jointUnseenAt: null,
    poseLostMs: 0,
    longTaskCount: 0,
    longTaskMaxMs: 0,
    heightRatioHist: new Uint32Array(HR_BUCKETS),
    heightRatioCount: 0,
    heightRatioMin: Infinity,
    heightRatioMax: -Infinity,
    tiltOscillations: 0,
    lastTiltDirection: null,
    rescuedTicks,
    heightRatioInBandMs: 0,
    missingMs,
    undetectedMs,
    missMax,
    missSum,
    missCount,
    inAreaMs: new Float64Array(JOINTS.length + 1),
    scoreHist: new Uint32Array(JOINTS.length * SCORE_BUCKETS),
    scoreCount: new Uint32Array(JOINTS.length),
    scoreMax: new Float64Array(JOINTS.length),
    sepHist: new Uint32Array(SEP_BUCKETS),
    sepCount: 0,
    sepMin: Infinity,
    correctPostureMs: 0,
    anklesCollapsedMs: 0,
    gapHist: new Uint32Array(GAP_BUCKETS),
    gapMaxMs: 0,
    rateRing: new Float64Array(RATE_WINDOW),
    rateHead: 0,
    rateCount: 0,
    phoneRing: new Float64Array(RATE_WINDOW),
    phoneHead: 0,
    phoneCount: 0,
    phoneFrameCounted: false,
  };
}

/**
 * `guidance` is a tri-state and all three states mean different things:
 *   a string  — a correction is being shown ("Move Back", "Tilt Device Up", …)
 *   null      — everything is good, nothing is shown
 *   undefined — indeterminate: the detector has no usable pose yet
 * Collapsing null and undefined would hide "the app had nothing to say because
 * it could not see you", which is a different user problem from "you are fine".
 */
function guidanceKey(message: unknown): string {
  if (message === null) return "ok";
  if (typeof message === "string" && message) return message;
  return "unknown";
}

function start(): void {
  stopListeners();
  const now = performance.now();
  acc = freshAcc(now);

  const poseManager = PoseDetectionManager.getInstance();
  poseManagerRef = poseManager;
  // `strictPostureSeen` describes ONE attempt, and
  // `PoseDetectionManager.start()` — where it is otherwise cleared — is not
  // called on the background-and-return path that `phases.ts` re-enters this
  // phase on. This is the boundary the summary rows are keyed on.
  poseManager.resetAttemptMetrics();

  unsubscribers.push(
    poseManager.on("jointCoordinates", (coords: any) => {
      const a = acc;
      if (!a) return;
      const t = performance.now();
      const dt = t - a.lastTickAt;
      a.lastTickAt = t;
      a.ticks++;
      // A long gap is not time the joints were out of position — it is time we
      // could not see them. Attributing it to `outMs` would make a user who
      // walked out of frame look like a user who could not aim their wrists.
      //
      // ── Why this is a branch and not a `return` ──────────────────────────
      //
      // It used to `return`, which silently made every measurement below
      // conditional on the PREVIOUS emit having been recent. On a device slow
      // enough that consecutive emits are more than POSE_LOST_GAP_MS apart,
      // that is EVERY emit, and the row comes back with no data at all — on
      // exactly the devices this instrument exists to describe. Measured on a
      // dev build: 64.2 s, 31 emits, 62.3 s charged to `poseLostMs`, and not
      // one keypoint score recorded.
      //
      // Durations still must not be charged across a gap. So the gap zeroes
      // `charge` instead of skipping the block: every `+= charge` below is a
      // DURATION and stays honest, while every per-SAMPLE fact is recorded from
      // a frame we genuinely saw.
      const lost = dt > POSE_LOST_GAP_MS;
      if (lost) {
        a.poseLostMs += dt;
      } else {
        // The gap TAIL, not just the mean. One bucket increment and a compare.
        const gapBucket = Math.min(
          GAP_BUCKETS - 1,
          Math.floor(dt / GAP_BUCKET_MS)
        );
        a.gapHist[gapBucket]++;
        if (dt > a.gapMaxMs) a.gapMaxMs = dt;
      }

      /**
       * Milliseconds this emit may charge to a duration: `dt` for an interval
       * we watched, 0 across a dropout. Every `+= charge` below was a `+= dt`
       * behind the old early return and is numerically identical for any
       * session without dropouts.
       */
      const charge = lost ? 0 : dt;

      // `correctPosture` is emitted BEFORE `jointCoordinates`, so this already
      // describes the current emit — the same ordering `gateBlockedOkMs` relies
      // on. `correctPostureLostCount` says how often the gate dropped; this says
      // how long it ever held, which is what separates "true once for 200 ms"
      // from "true for 40 s while something downstream kept resetting".
      if (a.correctPosture) a.correctPostureMs += charge;

      // Two writes and a modulo. The ring is pre-allocated; this must stay on
      // the NOT-lost side so a dropout cannot be read back as a slow-but-alive
      // device — that is the distinction that keeps BUG-0009 out of BUG-0008's
      // population.
      if (!lost) {
        const onPhone = poseManagerRef
          ? poseManagerRef.getLastDetectSource() === "device"
          : true;
        a.rateRing[a.rateHead] = t;
        a.rateHead = (a.rateHead + 1) % RATE_WINDOW;
        if (a.rateCount < RATE_WINDOW) a.rateCount++;
        // Auto's "phone too slow" is judged on the phone's own frames only.
        // Counting the cloud's made every cloud attempt read as a slow phone.
        if (onPhone) {
          recordPhoneFrame(a, t);
          a.phoneFrameCounted = true;
        } else {
          a.phoneCount = 0;
        }
      }

      // One field read, one bucket increment, two compares. `heightRatio`
      // refreshes every 4th processed frame, so this oversamples slightly —
      // harmless for a distribution, and far cheaper than a second subscription.
      const hr = poseManagerRef ? poseManagerRef.getHeightRatio() : 0;
      if (hr > 0) {
        const b = Math.min(HR_BUCKETS - 1, Math.floor(hr / HR_BUCKET_WIDTH));
        a.heightRatioHist[b]++;
        a.heightRatioCount++;
        if (hr < a.heightRatioMin) a.heightRatioMin = hr;
        if (hr > a.heightRatioMax) a.heightRatioMax = hr;
        if (hr > HR_BAND_MIN && hr <= HR_BAND_MAX) a.heightRatioInBandMs += charge;
      }

      // Auto's "the running model cannot see a joint" (liveHealth.ts). Fed from
      // these exact values ON PURPOSE: the trigger's thresholds were set from
      // prod `jointUndetectedMs` / `heightRatioInBandMs`, so it must judge
      // "unseen" and "framed" the same way those fields do. A joint seen
      // outside its box is NOT unseen — that is placement, not the model.
      sight.source =
        poseManagerRef && poseManagerRef.getLastDetectSource() === "server"
          ? "server"
          : "device";
      sight.chargeMs = charge;
      sight.framed = hr > HR_BAND_MIN && hr <= HR_BAND_MAX;
      for (let i = 0; i < JOINTS.length; i++) {
        const jd = coords ? coords[JOINTS[i]] : null;
        sight.unseen[i] = !jd || !jd.area || !jd.isDetected;
      }
      jointUnseenWhileFramed.observe(sight);
      if (a.jointUnseenAt === null && jointUnseenWhileFramed.unseenSource() !== null) {
        a.jointUnseenAt = Math.round(t - a.startedAt);
      }

      let inAreaCount = 0;
      for (let i = 0; i < JOINTS.length; i++) {
        const joint = JOINTS[i] as Joint;
        const jd = coords ? coords[joint] : null;
        const area = jd && jd.area;
        // Recorded BEFORE the `!area` continue, so the denominator is "every
        // emit that carried an estimate for this joint", which is the
        // population the question is about. One clamp, one bucket increment,
        // two adds and a compare — no allocation.
        if (jd && typeof jd.score === "number") {
          const sc = jd.score;
          let sb = Math.floor(sc * SCORE_BUCKETS_PER_UNIT);
          if (sb < 0) sb = 0;
          else if (sb >= SCORE_BUCKETS) sb = SCORE_BUCKETS - 1;
          a.scoreHist[i * SCORE_BUCKETS + sb]++;
          a.scoreCount[i]++;
          if (sc > a.scoreMax[i]) a.scoreMax[i] = sc;
        }
        if (!area) {
          // The detector returned no keypoint for this joint at all, so
          // `ScanCanvas` draws nothing for it — not a red box, NO box. Charging
          // it to `outMs` would read as a user who could not place the limb;
          // charging it nowhere (which is what happened before 2.5.15) loses the
          // single most diagnostic state the alignment screen has.
          a.missingMs[joint] += charge;
          continue;
        }
        if (!jd.isDetected) a.undetectedMs[joint] += charge;
        if (area.isJointInArea) {
          inAreaCount++;
          a.everIn[joint] = true;
          // Always false on the standard path — the field only exists on the
          // low-end path, where it answers whether the inflation earns its risk.
          if (area.rescuedByInflation) a.rescuedTicks[joint]++;
        } else {
          a.outMs[joint] += charge;
          // How FAR out, as a fraction of the box's own size — device
          // independent, and directly comparable with `boxInflateRatio`.
          const b = area.boundary;
          if (b) {
            const w = b.maxX - b.minX;
            const h = b.maxY - b.minY;
            const dx = w > 0 ? Math.max(0, b.minX - jd.x, jd.x - b.maxX) / w : 0;
            const dy = h > 0 ? Math.max(0, b.minY - jd.y, jd.y - b.maxY) / h : 0;
            const miss = dx > dy ? dx : dy;
            if (miss > a.missMax[joint]) a.missMax[joint] = miss;
            a.missSum[joint] += miss;
            a.missCount[joint]++;
          }
        }
      }
      a.inAreaMs[inAreaCount] += charge;

      // Both ankle estimates closer together than the gap between their target
      // boxes, i.e. it was geometrically IMPOSSIBLE for both to be inside at
      // once. That is a detector failure no gate relaxation can fix, and it is
      // what "moving one leg in made the other's box vanish" looks like.
      const la = coords ? coords.left_ankle : null;
      const ra = coords ? coords.right_ankle : null;
      if (la && ra && la.area && ra.area && la.area.boundary && ra.area.boundary) {
        const lb = la.area.boundary;
        const rb = ra.area.boundary;
        const gap = lb.minX - rb.maxX;
        const separation = Math.abs(la.x - ra.x);
        if (gap > 0 && separation < gap) a.anklesCollapsedMs += charge;

        // The same two estimates as a RATIO against what the boxes ask for, so
        // the reading is device- and screen-independent and its DISTRIBUTION —
        // not just its duration — is available. `anklesCollapsedMs` alone
        // cannot separate a person standing with their feet together from a
        // detector resolving both ankles onto one leg: same duration, very
        // different distribution. Centres are recomputed from the boundary
        // rather than read off `area.center`, so this depends on one shape.
        const centreGap = (lb.minX + lb.maxX) / 2 - (rb.minX + rb.maxX) / 2;
        if (centreGap > 0) {
          const ratio = separation / centreGap;
          let pb = Math.floor(ratio * SEP_BUCKETS_PER_UNIT);
          if (pb < 0) pb = 0;
          else if (pb >= SEP_BUCKETS) pb = SEP_BUCKETS - 1;
          a.sepHist[pb]++;
          a.sepCount++;
          if (ratio < a.sepMin) a.sepMin = ratio;
        }
      }
    })
  );

  // Every frame the alignment loop detected, sent after that frame's
  // `jointCoordinates`. The emits above are only the frames where a person box
  // and a pose were both found, so they alone cannot say how fast the phone's
  // detector runs: a person flickering at the edge of the frame leaves ~400 ms
  // holes in a 13 Hz stream, and on 2.5.21-2.5.25 that moved fast iPhones to
  // the cloud as "phone too slow".
  unsubscribers.push(
    poseManager.on("frameDetected", (source: "device" | "server") => {
      const a = acc;
      if (!a) return;
      if (source !== "device") {
        // The cloud's frame ends the phone's run, whether it emitted or not.
        a.phoneCount = 0;
        a.phoneFrameCounted = false;
        return;
      }
      if (a.phoneFrameCounted) {
        // Its emit, a moment ago, already counted this frame.
        a.phoneFrameCounted = false;
        return;
      }
      // No person box, no pose, or an emit after a long gap: the detector did
      // its work all the same.
      recordPhoneFrame(a, performance.now());
    })
  );

  unsubscribers.push(
    poseManager.on("correctPosture", (ok: boolean) => {
      const a = acc;
      if (!a) return;
      if (ok && a.firstCorrectPostureAt === null) {
        a.firstCorrectPostureAt = Math.round(performance.now() - a.startedAt);
      }
      if (a.correctPosture && !ok) a.correctPostureLostCount++;
      a.correctPosture = ok;
    })
  );

  unsubscribers.push(
    poseManager.on("guidance", (message: unknown) => {
      const a = acc;
      if (!a) return;
      const key = guidanceKey(message);
      if (key === a.lastGuidance) return;
      const t = performance.now();
      a.guidanceMs[a.lastGuidance] =
        (a.guidanceMs[a.lastGuidance] || 0) + (t - a.lastGuidanceAt);
      // Alternating Tilt Down <-> Tilt Up is the signature of a person who does
      // not FIT the field of view at their current distance: tilting to include
      // the head pushes the feet out and vice versa. Distinct from generic
      // thrash, so it gets its own counter.
      const dir =
        key === "Tilt Device Down" ? "down" : key === "Tilt Device Up" ? "up" : null;
      if (dir && a.lastTiltDirection && dir !== a.lastTiltDirection) {
        a.tiltOscillations++;
      }
      if (dir) a.lastTiltDirection = dir;

      a.lastGuidance = key;
      a.lastGuidanceAt = t;
      a.guidanceTransitions++;
    })
  );

  // Chromium-only. Absent on every iOS browser — the fields stay 0 there, which
  // is why `EVENTS.md` says to read them alongside `effectivePoseHz` rather than
  // on their own.
  try {
    if (typeof PerformanceObserver === "function") {
      longTaskObserver = new PerformanceObserver((list) => {
        const a = acc;
        if (!a) return;
        for (const entry of list.getEntries()) {
          a.longTaskCount++;
          if (entry.duration > a.longTaskMaxMs) {
            a.longTaskMaxMs = Math.round(entry.duration);
          }
        }
      });
      longTaskObserver.observe({ entryTypes: ["longtask"] });
    }
  } catch {
    longTaskObserver = null;
  }
}

function stopListeners(): void {
  for (const off of unsubscribers) {
    try {
      off();
    } catch {
      /* ignore */
    }
  }
  unsubscribers = [];
  try {
    if (longTaskObserver) longTaskObserver.disconnect();
  } catch {
    /* ignore */
  }
  longTaskObserver = null;
}

function finish(outcome: Outcome, attempt: number | null): void {
  const a = acc;
  acc = null;
  stopListeners();
  if (!a) return;

  const t = performance.now();
  const durationMs = Math.max(0, Math.round(t - a.startedAt));
  a.guidanceMs[a.lastGuidance] =
    (a.guidanceMs[a.lastGuidance] || 0) + (t - a.lastGuidanceAt);

  const guidanceMs: Record<string, number> = {};
  for (const k of Object.keys(a.guidanceMs)) {
    guidanceMs[k] = Math.round(a.guidanceMs[k]);
  }
  const jointOutOfAreaMs: Record<string, number> = {};
  const jointNeverInArea: string[] = [];
  const inflationRescuedTicks: Record<string, number> = {};
  const jointMissingMs: Record<string, number> = {};
  const jointUndetectedMs: Record<string, number> = {};
  const jointMissMaxRatio: Record<string, number> = {};
  const jointMissMeanRatio: Record<string, number> = {};
  const r2 = (n: number) => Math.round(n * 100) / 100;
  for (const j of JOINTS) {
    jointOutOfAreaMs[j] = Math.round(a.outMs[j]);
    inflationRescuedTicks[j] = a.rescuedTicks[j];
    jointMissingMs[j] = Math.round(a.missingMs[j]);
    jointUndetectedMs[j] = Math.round(a.undetectedMs[j]);
    jointMissMaxRatio[j] = r2(a.missMax[j]);
    jointMissMeanRatio[j] = a.missCount[j]
      ? r2(a.missSum[j] / a.missCount[j])
      : 0;
    if (!a.everIn[j]) jointNeverInArea.push(j);
  }
  const jointsInAreaMs: number[] = [];
  for (let n = 0; n < a.inAreaMs.length; n++) {
    jointsInAreaMs.push(Math.round(a.inAreaMs[n]));
  }
  /**
   * The bucket holding the p-th sample of a fixed histogram, reported at its
   * LOWER edge — the same convention `heightRatioP50` uses, so the two read
   * alike. `null` when the histogram is empty: never measured, not zero.
   *
   * The edge is `b / perUnit`, matching the integer multiplication the buckets
   * were assigned with, so a value that sat exactly on an edge reports that
   * edge back. See the note on `SCORE_BUCKETS`.
   */
  const histPct = (
    hist: Uint32Array,
    offset: number,
    buckets: number,
    count: number,
    perUnit: number,
    p: number
  ): number | null => {
    if (count <= 0) return null;
    const target = Math.floor((p / 100) * count);
    let seen = 0;
    for (let b = 0; b < buckets; b++) {
      seen += hist[offset + b];
      if (seen > target) return Math.round((b / perUnit) * 100) / 100;
    }
    return null;
  };

  // Per-joint keypoint confidence. `null` means the joint never carried an
  // estimate at all in this attempt — distinct from a score of 0.
  const jointScoreP50: Record<string, number | null> = {};
  const jointScoreMax: Record<string, number | null> = {};
  for (let i = 0; i < JOINTS.length; i++) {
    const j = JOINTS[i];
    const n = a.scoreCount[i];
    jointScoreP50[j] = histPct(
      a.scoreHist,
      i * SCORE_BUCKETS,
      SCORE_BUCKETS,
      n,
      SCORE_BUCKETS_PER_UNIT,
      50
    );
    jointScoreMax[j] = n > 0 ? r2(a.scoreMax[i]) : null;
  }

  const ankleSeparationP50Ratio = histPct(
    a.sepHist,
    0,
    SEP_BUCKETS,
    a.sepCount,
    SEP_BUCKETS_PER_UNIT,
    50
  );
  const ankleSeparationMinRatio = a.sepCount > 0 ? r2(a.sepMin) : null;

  // Read once, at exit. `{width: 0, height: 0}` before any frame was processed.
  // Guarded on the METHOD, not just the ref: this runs inside the one call that
  // writes the summary, so a manager without it would take the whole row down
  // rather than lose one field.
  const frameSize =
    poseManagerRef && poseManagerRef.getPoseFrameSize
      ? poseManagerRef.getPoseFrameSize()
      : { width: 0, height: 0 };

  // p90 of the inter-emit gap, at bucket resolution, reported at the bucket's
  // upper edge. `gapMaxMs` is exact.
  let gapTotal = 0;
  for (let b = 0; b < GAP_BUCKETS; b++) gapTotal += a.gapHist[b];
  let gapP90Ms = 0;
  if (gapTotal > 0) {
    const target = Math.floor(0.9 * gapTotal);
    let seen = 0;
    for (let b = 0; b < GAP_BUCKETS; b++) {
      seen += a.gapHist[b];
      if (seen > target) {
        gapP90Ms = (b + 1) * GAP_BUCKET_MS;
        break;
      }
    }
  }

  // Read at exit rather than at entry so a mid-attempt promotion to the
  // assisted path is reflected in the row that describes that attempt.
  const device = getDeviceMetrics();
  const tuning = poseManagerRef ? poseManagerRef.getTuning() : null;

  const activeMs = Math.max(1, durationMs - Math.round(a.poseLostMs));

  const measured = a.heightRatioCount > 0;
  // The bucket holding the p-th percentile sample, reported at its lower edge.
  const pct = (p: number): number | null => {
    if (!measured) return null;
    const target = Math.floor((p / 100) * a.heightRatioCount);
    let seen = 0;
    for (let b = 0; b < HR_BUCKETS; b++) {
      seen += a.heightRatioHist[b];
      if (seen > target) return Math.round(b * HR_BUCKET_WIDTH * 100) / 100;
    }
    return null;
  };
  const heightRatioMin = measured ? Math.round(a.heightRatioMin * 1000) / 1000 : null;
  const heightRatioMax = measured ? Math.round(a.heightRatioMax * 1000) / 1000 : null;

  logEvent(Events.SCAN_ALIGNMENT_SUMMARY, "[telemetry] alignment summary", {
    phase: Phases.ALIGNMENT,
    outcome,
    durationMs,
    data: {
      attempt,
      jointOutOfAreaMs,
      jointNeverInArea,
      guidanceMs,
      guidanceTransitions: a.guidanceTransitions,
      correctPostureLostCount: a.correctPostureLostCount,
      timeToFirstCorrectPostureMs: a.firstCorrectPostureAt,
      poseLostMs: Math.round(a.poseLostMs),
      // Measured against time a pose was actually visible, not wall clock —
      // otherwise a user who stepped out of frame reads as a slow device.
      effectivePoseHz: Math.round((a.ticks / activeMs) * 1000 * 10) / 10,
      /**
       * Emits per second of WALL CLOCK — the rate the user actually
       * experienced.
       *
       * `effectivePoseHz` above divides by `durationMs - poseLostMs` and
       * **inverts on a device slow enough to be the problem**: once consecutive
       * emits are more than POSE_LOST_GAP_MS apart, every gap is charged to
       * `poseLostMs`, the denominator collapses and the rate reads HIGH. A dev
       * run at a true 0.48 Hz (31 emits in 64.2 s) reported 16.3 — better than
       * a working iPhone. Added rather than corrected so the `effectivePoseHz`
       * series stays comparable; read this one first.
       */
      poseHz: Math.round((a.ticks / Math.max(1, durationMs)) * 1000 * 10) / 10,
      ticks: a.ticks,
      /**
       * Per-joint keypoint confidence — the number `keyPointInFrame` compares
       * against 0.3, at 0.05 resolution with the threshold deliberately ON a
       * bucket edge. `jointUndetectedMs` only reports the RESULT of that
       * comparison, which cannot tell a joint at 0.05 from one at 0.29.
       * `null` = the joint carried no estimate at all, NOT a score of zero.
       */
      jointScoreP50,
      jointScoreMax,
      /**
       * The two ankle estimates' horizontal separation as a fraction of the
       * distance between their box centres. 1.0 = exactly as far apart as the
       * boxes ask. Separates the two causes of a large `anklesCollapsedMs`: a
       * narrow stance is stable, a collapsing detector is not. `null` = never
       * measured.
       */
      ankleSeparationP50Ratio,
      ankleSeparationMinRatio,
      /**
       * Pixel size of the frame handed to the pose model. MoveNet resizes
       * whatever it gets to 192x192, so `max(w,h)/192` is the downsample factor
       * the keypoints were produced at. `0` = no frame was processed.
       */
      poseFrameWidth: frameSize.width,
      poseFrameHeight: frameSize.height,
      longTaskCount: a.longTaskCount,
      longTaskMaxMs: a.longTaskMaxMs,

      // What the app SAW, alongside what it SAID. `isTooClose` is
      // heightRatio > 0.95 and `isTooFar` is <= 0.6, so these bound the guidance.
      heightRatioMin,
      heightRatioP50: pct(50),
      heightRatioMax,
      // The decisive one: the user never got themselves small enough in frame
      // even at their best moment, so "Move Back" was unfollowable — they ran
      // out of room, or they do not fit this camera's field of view. That is a
      // physical-environment problem, not an instruction problem, and no amount
      // of copy changes will fix it.
      neverFitFrame: heightRatioMin !== null && heightRatioMin > 0.95,
      tiltOscillations: a.tiltOscillations,

      // ── BUG-0008 ────────────────────────────────────────────────────────
      // These land in the SAME document as `effectivePoseHz`, which is the
      // whole point: the join that picks the device-class thresholds — measured
      // cycle time against what the session actually achieved — is then within
      // one row and needs no query gymnastics.
      //
      // `null` on the cycle times means never measured, NOT zero — the same
      // distinction `heightRatio*` makes just above.
      deviceClass: device.deviceClass,
      deviceCycleMedianMs: device.cycleMedianMs,
      deviceCycleMaxMs: device.cycleMaxMs,
      deviceClassSource: device.source,
      promotedLive: device.promotedLive,
      /**
       * The isolated main-thread EfficientDet cost, and how many cycles the
       * verdict rests on. Together they let a reader separate the pose-only
       * cost from the mixed cost — which matters because the 250 ms threshold
       * is a ROTATION deadline, and rotation never runs the object pass
       * (`detectRotationPose` is pose-only, and `startCountdown` pauses the
       * alignment loop). Without these the measured cycle is known to be
       * pessimistic against that threshold but not by how much.
       */
      deviceObjectPassMs: device.objectPassMs,
      deviceMeasuredCycles: device.measuredCycles,
      /** The relaxations were actually in force for this attempt. */
      assistedPath: !!tuning && tuning.jointLatchMs > 0,
      /**
       * Today's exact gate — un-latched, un-inflated — was satisfied at least
       * once. Pair with the presence of a `scan.rotation.summary` row in the
       * same journey to find the case worth watching: a scan that completed
       * without the user ever once being strictly in position. The backend has
       * no runtime pose validator, so that surfaces as a wrong avatar rather
       * than as an error.
       */
      strictPostureSeen: poseManagerRef
        ? poseManagerRef.hasSeenStrictPosture()
        : false,
      /**
       * The counterfactual: emits a 600 ms per-joint latch WOULD have converted
       * from false to true, measured while the gate itself behaves exactly as
       * 2.5.13. Nothing in this release applies that latch — this is how the
       * next release's headline mechanism gets judged on production traffic
       * before it ships to anybody.
       */
      latchRescuedEmits: poseManagerRef
        ? poseManagerRef.getLatchRescuedEmits()
        : 0,
      inflationRescuedTicks,

      // ── Real-frame benchmark + live health (spec 2026-09-11) ────────────
      // On the row they are meant to predict / describe, so "did the startup
      // verdict predict this outcome" and "did the phone break mid-scan" are
      // filters rather than joins. Full benchmark detail: `device.benchmark`.
      benchmarkVerdict: device.benchmark ? device.benchmark.verdict : null,
      benchmarkUnknownReason: device.benchmark ? device.benchmark.unknownReason : null,
      benchmarkGatedDevMaxRatio: device.benchmark ? device.benchmark.gatedDevMaxRatio : null,
      benchmarkConfidenceRatioMin: device.benchmark ? device.benchmark.confidenceRatioMin : null,
      float32Enabled: poseManagerRef ? poseManagerRef.getFloat32Enabled() : null,
      detectorErrors: detectorHealth.errors(),
      detectorBroken: detectorHealth.isBroken(),
      blindWhileVisibleMs: blindWhileVisible.blindMs(),
      jointUnseenSource: jointUnseenWhileFramed.unseenSource(),
      jointUnseenAtMs: a.jointUnseenAt,
      jointUnseenWorstMs: jointUnseenWhileFramed.worstUnseenMs(),
      // The longest run of identical analysed frames. >= 2000 ms is a frozen
      // camera, and the scan was stopped for it (videoFreeze.ts).
      videoFrozenMs: alignmentVideoFreeze.frozenMs(),

      // ── v2.5.15 diagnostics ─────────────────────────────────────────────
      /**
       * Time the detector returned NO keypoint for a joint. `ScanCanvas` bails
       * before drawing anything in that state, so the user loses the target box
       * itself rather than seeing it turn red. Before 2.5.15 this time was
       * charged to no field at all — the accumulator skipped a null joint — so
       * "one of the four boxes just disappears" was invisible in telemetry.
       */
      jointMissingMs,
      /**
       * Time a joint was present but unusable: `keyPointInFrame` false, i.e.
       * score <= 0.3 or pinned at the origin. The GATE ignores confidence and
       * the joint LATCH requires it, so this is also the field that says
       * whether a latch could ever have helped a given device.
       */
      jointUndetectedMs,
      /**
       * How far outside its box a joint sat when it was outside, as a fraction
       * of that box's own size — so it is comparable across screen sizes and
       * directly against `boxInflateRatio`. `jointOutOfAreaMs` says how LONG and
       * never how FAR, which cannot separate "the box is slightly wrong" from
       * "the detector is not tracking this joint".
       */
      jointMissMaxRatio,
      jointMissMeanRatio,
      /**
       * Visible time with N of the four joints simultaneously inside their
       * boxes, indexed 0..4. A session that spends a minute at 3 and a fraction
       * of a second at 4 IS BUG-0008, with no inference required — it is the
       * user-visible symptom ("I only ever saw three greens") as a number.
       */
      jointsInAreaMs,
      /** Visible time the emitted gate was true, against how often it dropped. */
      correctPostureMs: Math.round(a.correctPostureMs),
      /**
       * Time both ankle estimates sat closer together than the gap between
       * their target boxes — i.e. it was geometrically impossible for both to be
       * inside at once. A detector failure no gate relaxation can fix.
       */
      anklesCollapsedMs: Math.round(a.anklesCollapsedMs),
      /** The emit-gap tail. `effectivePoseHz` is a mean and hides a stutter. */
      tickGapP90Ms: gapP90Ms,
      tickGapMaxMs: Math.round(a.gapMaxMs),
      /**
       * Visible time inside the followable heightRatio band. Zero means the
       * person never once got themselves properly into frame, which is a
       * framing/lighting problem and not a device one — the discriminator that
       * stops a badly-framed session being blamed on the handset.
       */
      heightRatioInBandMs: Math.round(a.heightRatioInBandMs),

      // ── Server-side pose ────────────────────────────────────────────────
      // Fields on the EXISTING row, never a new event, so a server-routed
      // attempt and a device one differ by a FILTER rather than a join. All of
      // them are inert on the device path: `poseSource` reads "device" and the
      // counters are 0 / null, which is what every row before this build
      // already meant.
      ...(poseManagerRef ? poseManagerRef.getServerPoseStats() : {}),
    },
  });
}

/**
 * Emit rate over the trailing `windowMs`, measured only across gaps short
 * enough to mean "we could see them" — the same `POSE_LOST_GAP_MS` rule
 * `effectivePoseHz` uses, so the two numbers are directly comparable.
 *
 * This is the LIVE signal that is allowed to override the boot-time device
 * verdict. It exists because a boot measurement is a sample of one moment: a
 * busy CPU at page load, four competing tabs (the confirmed reporter had five),
 * or a phone that is cool at boot and thermally throttled three minutes later.
 *
 * Returns null until there is enough of a window to be worth acting on.
 *
 * @param opts.phoneOnly the rate of the PHONE's own frames instead: every frame
 *   its detector completed, person found or not — what Auto's "phone too slow"
 *   judges. The emits this reads by default need a person box and a pose, so
 *   they also measure the framing. A frame the cloud detected starts the
 *   phone's frames over. Between two of them a gap over `POSE_LOST_GAP_MS` is a
 *   stall, not the phone's pace: it is left out, and the frames before it
 *   still count — breaking there would restart the evidence on every stall and
 *   never judge a slow phone that also stalls.
 * @param opts.minSpanMs null until the gaps counted add up to this much.
 */
export function getRecentPoseHz(
  windowMs: number,
  opts?: { phoneOnly?: boolean; minSpanMs?: number }
): number | null {
  const a = acc;
  if (!a) return null;
  const minSpanMs = (opts && opts.minSpanMs) || 0;
  return opts && opts.phoneOnly
    ? ringHz(a.phoneRing, a.phoneHead, a.phoneCount, windowMs, true, minSpanMs)
    : ringHz(a.rateRing, a.rateHead, a.rateCount, windowMs, false, minSpanMs);
}

/** `getRecentPoseHz` over one ring, walked newest → oldest. */
function ringHz(
  ring: Float64Array,
  head: number,
  count: number,
  windowMs: number,
  skipStalls: boolean,
  minSpanMs: number
): number | null {
  if (count < 3) return null;
  const now = performance.now();
  let gaps = 0;
  let spanMs = 0;
  // `null`, not 0: `performance.now()` can legitimately return 0, and this
  // codebase has already been bitten twice by a sentinel that collides with a
  // real reading (`deviceCapability.startedAt`,
  // `PoseDetectionManager.lastEmitAt`). A zero-valued first sample would
  // otherwise be skipped as "no previous sample" and its gap never counted.
  let prev: number | null = null;
  for (let i = 1; i <= count; i++) {
    const t = ring[(head - i + RATE_WINDOW) % RATE_WINDOW];
    if (now - t > windowMs) break;
    if (prev !== null) {
      const gap = prev - t;
      if (gap > POSE_LOST_GAP_MS) {
        // Between emits, a long gap is time we could not SEE them, not time
        // the device was slow. Charging it here would make BUG-0009 look like
        // BUG-0008. Between the phone's own frames it is a stall: skipped.
        if (!skipStalls) break;
      } else {
        gaps++;
        spanMs += gap;
      }
    }
    prev = t;
  }
  if (gaps < 2 || spanMs <= 0 || spanMs < minSpanMs) return null;
  return Math.round((gaps / spanMs) * 1000 * 10) / 10;
}

/**
 * One frame the phone's detector completed, and Auto's verdict on it. Two
 * writes and a modulo, like the emit ring.
 *
 * Judged only once the phone has run `PHONE_RATE_MIN_SPAN_MS` of the window:
 * from three frames, one late frame was a verdict, and the latch made it the
 * answer for the attempt.
 */
function recordPhoneFrame(a: Acc, t: number): void {
  a.phoneRing[a.phoneHead] = t;
  a.phoneHead = (a.phoneHead + 1) % RATE_WINDOW;
  if (a.phoneCount < RATE_WINDOW) a.phoneCount++;
  phoneTooSlow.observe(
    getRecentPoseHz(PHONE_RATE_WINDOW_MS, {
      phoneOnly: true,
      minSpanMs: PHONE_RATE_MIN_SPAN_MS,
    })
  );
}

let bound = false;

/**
 * Attach to the phase model. Idempotent; call once at bootstrap.
 *
 * Driven by phase transitions rather than by the scan screen so that a summary
 * is still emitted when the phase is closed by `pagehide` or
 * `visibilitychange` — the abandoned and backgrounded sessions are the ones
 * worth having a struggle record for, and a screen-level hook never runs for
 * them.
 */
export function bindAlignmentAccumulator(): void {
  if (bound) return;
  bound = true;
  onPhaseTransition((kind, phase, outcome, _durationMs, attempt) => {
    if (phase !== Phases.ALIGNMENT) return;
    if (kind === "enter") start();
    else finish(outcome || Outcomes.ABANDONED, attempt);
  });
}

/** Test-only. */
export function __resetAlignmentAccumulatorForTest(): void {
  acc = null;
  stopListeners();
  bound = false;
}
